// Bulk fulfilment upload (Orders page ⋯ menu → Upload fulfilments).
//
// A courier's consignment sheet — one row per order with the order number,
// the courier and the tracking number — marks every order on it Fulfilled
// in one go. The file is parsed here (CSV or tab-separated, header row
// recognised by name, otherwise positional), each row is resolved to an
// order by the BE (POST /orders/fulfil/resolve?warehouse=…: single-child
// consolidated masters, "already done" repeats, tracking collisions), the
// operator reviews the match table and confirms, and the matched rows are
// then posted as ordinary POST /orders/bulk status rows (action update,
// status fulfilled, tracking_carrier, tracking_number) — the SAME path as
// Change status → Fulfilled with tracking typed in, so the manual parcel,
// the stock consumption and the channel notification all happen as they
// would there.
//
// Alex's rules (2026-11-04):
//   * no ticking — every row that resolves to an unfulfilled order goes;
//     the only choice left to the operator is a dropdown when two
//     unfulfilled orders carry the same number;
//   * one warehouse — orders are matched in the warehouse selected in the
//     Orders filter and nowhere else, because a Jobalots order split per
//     warehouse shares its number across them (MCL is the intended user);
//   * unfulfilled orders only — anything in progress is reported, not moved.
//
// Rows that cannot be fulfilled stay in the table with the reason, so the
// operator sees what was skipped. A row whose order is already fulfilled
// with the same tracking is reported as done, not an error: couriers resend
// whole sheets.
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle, Download, Loader2, PackageCheck, X } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../../api/apiFetch";
import { useTranslation } from "../../i18n";

export const MAX_FULFIL_ROWS = 1000;

export interface SheetRow {
    line: number;            // 1-based line in the file, header included
    order_number: string;
    courier: string;
    tracking_number: string;
}

// Minimal delimited-text parser (quotes, doubled quotes, CRLF). The Orders
// CSV upload has its own copy for commas; this one also takes tabs, since
// courier sheets are usually pasted out of a spreadsheet.
export const parseDelimited = (text: string, delimiter: string): string[][] => {
    const rows: string[][] = [];
    let field = "";
    let row: string[] = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; }
                else inQuotes = false;
            } else field += ch;
        } else if (ch === '"') inQuotes = true;
        else if (ch === delimiter) { row.push(field); field = ""; }
        else if (ch === "\r") { /* skip */ }
        else if (ch === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
        else field += ch;
    }
    if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }
    return rows;
};

const HEADER_WORDS = /order|reference|consignment|courier|carrier|tracking/i;

// Sheet text → rows. A header row is recognised when any cell names one of
// the three columns; "tracking" wins the tracking column so "Consignment
// Number" next to "Tracking ID" reads as the order column (MCL's sheet),
// while a lone "Consignment" column is the tracking (Palletways' word).
// Without a header the columns are positional: order, courier, tracking —
// or order, tracking when there are only two.
export const parseFulfilSheet = (text: string): { rows: SheetRow[]; hadHeader: boolean } => {
    const clean = text.replace(/^﻿/, "");
    const firstLine = clean.split(/\r?\n/, 1)[0] ?? "";
    const delimiter = firstLine.includes("\t") ? "\t" : ",";
    const raw = parseDelimited(clean, delimiter);
    const nonEmpty = raw
        .map((cells, i) => ({ line: i + 1, cells: cells.map(c => c.trim()) }))
        .filter(r => r.cells.some(c => c !== ""));
    if (nonEmpty.length === 0) return { rows: [], hadHeader: false };

    const head = nonEmpty[0].cells.map(c => c.toLowerCase());
    const hadHeader = head.some(c => HEADER_WORDS.test(c));
    let orderCol = 0;
    let courierCol: number | null = null;
    let trackingCol: number | null = null;
    if (hadHeader) {
        trackingCol = head.findIndex(c => /tracking/.test(c));
        courierCol = head.findIndex(c => /courier|carrier/.test(c));
        const orderIdx = head.findIndex((c, i) => i !== trackingCol && i !== courierCol && /order|reference|consignment/.test(c));
        if (trackingCol < 0) {
            // No "tracking" header: a lone consignment column is the tracking.
            const con = head.findIndex((c, i) => i !== courierCol && /consignment/.test(c) && i !== orderIdx);
            trackingCol = con >= 0 ? con : null;
        }
        if (courierCol < 0) courierCol = null;
        orderCol = orderIdx >= 0 ? orderIdx : head.findIndex((_, i) => i !== trackingCol && i !== courierCol);
        if (orderCol < 0) orderCol = 0;
    } else {
        const width = Math.max(...nonEmpty.map(r => r.cells.length));
        if (width >= 3) { courierCol = 1; trackingCol = 2; }
        else if (width === 2) { trackingCol = 1; }
    }
    const body = hadHeader ? nonEmpty.slice(1) : nonEmpty;
    const rows = body.map(r => ({
        line: r.line,
        order_number: r.cells[orderCol] ?? "",
        courier: courierCol === null ? "" : (r.cells[courierCol] ?? ""),
        tracking_number: trackingCol === null ? "" : (r.cells[trackingCol] ?? ""),
    }));
    return { rows, hadHeader };
};

// A warehouse pill value that names ONE warehouse ("" = all, "country:X" = a group).
export const isSingleWarehouse = (warehouse: string): boolean =>
    warehouse.trim() !== "" && !warehouse.startsWith("country:");

export interface ResolvedOrder {
    order_id: string;
    order_number: string;
    warehouse: string;
    status: string;
    sales_channel: string | null;
    via?: string | null;
}

export type Outcome =
    | "ready"
    | "already_done"
    | "fulfilled_other"
    | "not_found"
    | "missing_order"
    | "ambiguous"
    | "in_consolidation"
    | "in_progress"
    | "closed"
    | "missing_tracking"
    | "duplicate_tracking"
    | "tracking_in_use";

interface ResolveResult {
    order_number: string;
    courier: string;
    tracking_number: string;
    outcome: Outcome;
    target: ResolvedOrder | null;
    via: string | null;
    candidates: ResolvedOrder[];
    note: string | null;
}

export type RowStatus = Outcome | "applying" | "done" | "failed";

export interface FulfilRow {
    id: number;
    sheet: SheetRow;
    status: RowStatus;
    target: ResolvedOrder | null;
    via: string | null;
    candidates: ResolvedOrder[];
    note: string | null;
    error: string | null;
}

// Rows that will be fulfilled: resolved to one unfulfilled order with a
// tracking number. An ambiguous row joins once a choice is made.
export const willFulfil = (row: FulfilRow): boolean => row.status === "ready" && row.target !== null;

interface BulkResult {
    updated: number;
    errors: { row: number; reason: string }[];
}

type Phase = "parsing" | "resolving" | "review" | "applying" | "done";

const TEMPLATE = "Order number,Courier,Tracking number\r\nJUK123456,Palletways,60051583583\r\n";

const downloadTemplate = () => {
    const blob = new Blob(["﻿" + TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "fulfilments_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const BulkFulfilUploadModal = ({
    file,
    warehouse,
    onClose,
    onApplied,
}: {
    file: File;
    warehouse: string;
    onClose: () => void;
    onApplied?: () => void;
}) => {
    const { t } = useTranslation();
    const singleWarehouse = isSingleWarehouse(warehouse);
    const [phase, setPhase] = useState<Phase>("parsing");
    const [rows, setRows] = useState<FulfilRow[]>([]);
    const [defaultCourier, setDefaultCourier] = useState("");
    const [resolveFailed, setResolveFailed] = useState(false);
    const [empty, setEmpty] = useState(false);
    // Confirmation step: the orders about to be fulfilled, with courier and
    // tracking, shown once more before anything is posted.
    const [confirming, setConfirming] = useState(false);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    // Parse then resolve once, on open. Without a single warehouse selected
    // there is nothing to match against, so the file is not even read.
    useEffect(() => {
        let alive = true;
        if (!singleWarehouse) { setPhase("review"); return () => { alive = false; }; }
        (async () => {
            let sheet: SheetRow[] = [];
            try {
                sheet = parseFulfilSheet(await file.text()).rows.slice(0, MAX_FULFIL_ROWS);
            } catch {
                sheet = [];
            }
            if (!alive) return;
            if (sheet.length === 0) {
                setEmpty(true);
                setPhase("review");
                return;
            }
            const base: FulfilRow[] = sheet.map((s, i) => ({
                id: i, sheet: s, status: "not_found", target: null, via: null, candidates: [], note: null, error: null,
            }));
            setRows(base);
            setPhase("resolving");
            try {
                const r = await apiFetch(`/orders/fulfil/resolve?warehouse=${encodeURIComponent(warehouse)}`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ rows: sheet.map(s => ({ order_number: s.order_number, courier: s.courier, tracking_number: s.tracking_number })) }),
                });
                if (!alive) return;
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data: { results: ResolveResult[] } = await r.json();
                if (!alive) return;
                setRows(base.map((row, i) => {
                    const res = data.results?.[i];
                    if (!res) return row;
                    return {
                        ...row,
                        status: res.outcome,
                        target: res.target,
                        via: res.via ?? null,
                        candidates: res.candidates ?? [],
                        note: res.note ?? null,
                    };
                }));
                setPhase("review");
            } catch {
                if (!alive) return;
                setResolveFailed(true);
                setPhase("review");
                toast.error(t("bulk_fulfil_resolve_failed"), { toastId: "bulk-fulfil-resolve-err" });
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]);

    const counts = useMemo(() => {
        const c = { ready: 0, already: 0, skipped: 0, done: 0, failed: 0 };
        for (const r of rows) {
            if (willFulfil(r)) c.ready += 1;
            else if (r.status === "already_done") c.already += 1;
            else if (r.status === "done") c.done += 1;
            else if (r.status === "failed") c.failed += 1;
            else if (r.status !== "applying") c.skipped += 1;
        }
        return c;
    }, [rows]);

    const queued = rows.filter(willFulfil);

    // Ambiguous row: picking a candidate makes it ready; clearing it puts it
    // back to ambiguous (never fulfilled).
    const chooseCandidate = (id: number, orderId: string) =>
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const pick = r.candidates.find(c => c.order_id === orderId) ?? null;
            return { ...r, target: pick, via: pick?.via ?? null, status: pick ? "ready" : "ambiguous" };
        }));

    const courierOf = (row: FulfilRow): string => row.sheet.courier.trim() || defaultCourier.trim();

    const apply = async () => {
        if (queued.length === 0) return;
        setConfirming(false);
        setPhase("applying");
        const ids = new Set(queued.map(r => r.id));
        setRows(prev => prev.map(r => (ids.has(r.id) ? { ...r, status: "applying", error: null } : r)));
        try {
            const body = queued.map(r => ({
                action: "update",
                order_id: (r.target as ResolvedOrder).order_id,
                status: "fulfilled",
                ...(courierOf(r) ? { tracking_carrier: courierOf(r) } : {}),
                tracking_number: r.sheet.tracking_number.trim(),
            }));
            const res = await apiFetch(`/orders/bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ rows: body }),
            });
            if (!mounted.current) return;
            if (res.status === 413) {
                toast.error(t("bulk_upload_too_large"), { toastId: "bulk-fulfil-413" });
                setRows(prev => prev.map(r => (ids.has(r.id) ? { ...r, status: "ready" } : r)));
                setPhase("review");
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const result: BulkResult = await res.json();
            // The BE numbers rows as in a CSV with a header: index + 2.
            const failedByIndex = new Map<number, string>();
            for (const e of result.errors ?? []) failedByIndex.set(e.row - 2, e.reason);
            let done = 0;
            let failed = 0;
            setRows(prev => prev.map(r => {
                const idx = queued.findIndex(q => q.id === r.id);
                if (idx < 0) return r;
                const reason = failedByIndex.get(idx);
                if (reason !== undefined) { failed += 1; return { ...r, status: "failed", error: reason }; }
                done += 1;
                return { ...r, status: "done" };
            }));
            setPhase("done");
            onApplied?.();
            if (failed === 0) toast.success(t("bulk_fulfil_toast_done", { n: String(done) }), { toastId: "bulk-fulfil-done" });
            else toast.error(t("bulk_fulfil_toast_partial", { done: String(done), failed: String(failed) }), { toastId: "bulk-fulfil-partial" });
        } catch {
            if (!mounted.current) return;
            setRows(prev => prev.map(r => (ids.has(r.id) ? { ...r, status: "failed", error: t("server_error") } : r)));
            setPhase("done");
            toast.error(t("server_error"), { toastId: "bulk-fulfil-err" });
        }
    };

    const busy = phase === "parsing" || phase === "resolving" || phase === "applying";

    const statusCell = (row: FulfilRow) => {
        const ok = (label: string) => <span className="bulk-labels-status bulk-labels-status--ok"><CheckCircle size={12} />{label}</span>;
        const warn = (label: string) => <span className="bulk-labels-status bulk-labels-status--warn">{label}</span>;
        const bad = (label: string, title?: string) => (
            <span className="bulk-labels-status bulk-labels-status--bad" title={title}><AlertCircle size={12} />{label}</span>
        );
        switch (row.status) {
            case "ready": return <span className="bulk-labels-status bulk-labels-status--ok">{t("bulk_fulfil_status_ready")}</span>;
            case "already_done": return ok(t("bulk_fulfil_status_already_done"));
            case "fulfilled_other": return warn(row.note
                ? t("bulk_fulfil_status_fulfilled_other", { tracking: row.note })
                : t("bulk_fulfil_status_fulfilled_none"));
            case "not_found": return bad(t("bulk_fulfil_status_not_found", { warehouse }));
            case "missing_order": return bad(t("bulk_fulfil_status_missing_order"));
            case "ambiguous": return warn(t("bulk_fulfil_status_ambiguous"));
            case "in_consolidation": return warn(t("bulk_fulfil_status_in_consolidation", { master: row.note ?? "" }));
            case "in_progress": return warn(t("bulk_fulfil_status_in_progress", { status: row.note ? t(`status_${row.note}`) : "" }));
            case "closed": return bad(t("bulk_fulfil_status_closed", { status: row.target ? t(`status_${row.target.status}`) : "" }));
            case "missing_tracking": return bad(t("bulk_fulfil_status_missing_tracking"));
            case "duplicate_tracking": return bad(t("bulk_fulfil_status_duplicate_tracking"));
            case "tracking_in_use": return bad(t("bulk_fulfil_status_tracking_in_use", { order: row.note ?? "" }));
            case "applying": return <span className="bulk-labels-status"><Loader2 size={12} className="spin" />{t("bulk_fulfil_status_applying")}</span>;
            case "done": return ok(t("bulk_fulfil_status_done"));
            case "failed": return bad(`${t("bulk_fulfil_status_failed")}${row.error ? ` — ${row.error}` : ""}`, row.error ?? undefined);
            default: return null;
        }
    };

    const orderCell = (row: FulfilRow) => {
        if (row.candidates.length > 1 && phase === "review") {
            return (
                <select
                    className="bulk-labels-select"
                    value={row.target?.order_id ?? ""}
                    disabled={busy}
                    aria-label={t("bulk_fulfil_choose_order")}
                    onChange={e => chooseCandidate(row.id, e.target.value)}
                >
                    <option value="">{t("bulk_fulfil_choose_order")}</option>
                    {row.candidates.map(c => (
                        <option key={c.order_id} value={c.order_id}>
                            {c.order_number}{c.via ? ` ${t("bulk_fulfil_via", { child: c.via })}` : ""} · {c.order_id}
                        </option>
                    ))}
                </select>
            );
        }
        if (!row.target) return <span className="bulk-labels-muted">{row.sheet.order_number || "—"}</span>;
        return (
            <span className="bulk-labels-order">
                <strong>{row.target.order_number}</strong>
                {row.via && <span className="bulk-labels-muted"> {t("bulk_fulfil_via", { child: row.via })}</span>}
                <span className="bulk-labels-muted"> · {t(`status_${row.target.status}`)}</span>
            </span>
        );
    };

    return (
        <div className="modal-overlay" onClick={busy ? undefined : onClose}>
            <div className="modal-card bulk-labels-modal bulk-fulfil-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{singleWarehouse ? t("bulk_fulfil_title_wh", { warehouse }) : t("bulk_fulfil_title")}</h2>
                    <button className="modal-close" onClick={onClose} disabled={busy} aria-label={t("cancel")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    {!singleWarehouse ? (
                        <p className="bulk-labels-summary bulk-labels-status--bad" data-testid="bulk-fulfil-summary">{t("bulk_fulfil_need_warehouse")}</p>
                    ) : (
                        <p className="bulk-labels-intro">
                            {t("bulk_fulfil_intro", { warehouse })}{" "}
                            <button type="button" className="bulk-fulfil-template" onClick={downloadTemplate}>
                                <Download size={12} /> {t("bulk_fulfil_template")}
                            </button>
                        </p>
                    )}

                    {!singleWarehouse ? null : phase === "parsing" || phase === "resolving" ? (
                        <div className="bulk-labels-resolving">
                            <Loader2 size={16} className="spin" />{" "}
                            {phase === "parsing" ? t("bulk_fulfil_parsing") : t("bulk_fulfil_resolving", { n: String(rows.length) })}
                        </div>
                    ) : empty ? (
                        <p className="bulk-labels-summary bulk-labels-status--bad" data-testid="bulk-fulfil-summary">{t("bulk_fulfil_empty")}</p>
                    ) : (
                        <>
                            <label className="bulk-fulfil-default-courier">
                                <span>{t("bulk_fulfil_default_courier")}</span>
                                <input
                                    type="text"
                                    value={defaultCourier}
                                    disabled={busy || phase === "done"}
                                    placeholder={t("manual_fulfil_carrier_placeholder")}
                                    onChange={e => setDefaultCourier(e.target.value)}
                                />
                            </label>
                            <p className="bulk-labels-summary" data-testid="bulk-fulfil-summary">
                                {phase === "done"
                                    ? t("bulk_fulfil_done_summary", { done: String(counts.done), failed: String(counts.failed) })
                                    : t("bulk_fulfil_summary", {
                                        ready: String(counts.ready),
                                        done: String(counts.already),
                                        skipped: String(counts.skipped),
                                    })}
                                {resolveFailed && <span className="bulk-labels-status bulk-labels-status--bad"> {t("bulk_fulfil_resolve_failed")}</span>}
                            </p>
                            <div className="bulk-labels-table-wrap">
                                <table className="bulk-labels-table">
                                    <thead>
                                        <tr>
                                            <th>{t("bulk_fulfil_col_row")}</th>
                                            <th>{t("bulk_fulfil_col_order")}</th>
                                            <th>{t("bulk_fulfil_col_courier")}</th>
                                            <th>{t("bulk_fulfil_col_tracking")}</th>
                                            <th>{t("bulk_fulfil_col_status")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(row => (
                                            <tr key={row.id} className={`bulk-labels-row bulk-labels-row--${row.status}`} data-testid={`bulk-fulfil-row-${row.sheet.order_number}`}>
                                                <td className="bulk-labels-muted">{row.sheet.line}</td>
                                                <td>{orderCell(row)}</td>
                                                <td>{courierOf(row) || <span className="bulk-labels-status bulk-labels-status--warn">—</span>}</td>
                                                <td className="bulk-labels-file" title={row.sheet.tracking_number}>{row.sheet.tracking_number || "—"}</td>
                                                <td>{statusCell(row)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <div className="modal-confirm-buttons" style={{ marginTop: 18 }}>
                        <button className="btn btn-ghost dispatch-modal-btn" onClick={onClose} disabled={busy}>
                            {phase === "done" ? t("close") : t("cancel")}
                        </button>
                        {phase !== "done" && !empty && singleWarehouse && (
                            <button
                                className="btn btn-success dispatch-modal-btn"
                                onClick={() => setConfirming(true)}
                                disabled={busy || queued.length === 0}
                            >
                                {phase === "applying" ? <Loader2 size={14} className="spin" /> : <PackageCheck size={14} />}
                                {phase === "applying" ? t("submitting") : t("bulk_fulfil_apply_n", { n: String(queued.length) })}
                            </button>
                        )}
                    </div>
                </div>
                {confirming && (
                    <div className="modal-overlay bulk-fulfil-confirm-overlay" onClick={() => setConfirming(false)}>
                        <div className="modal-card bulk-fulfil-confirm" role="dialog" aria-labelledby="bulk-fulfil-confirm-title" onClick={e => e.stopPropagation()}>
                            <div className="modal-card-header">
                                <h2 id="bulk-fulfil-confirm-title">{t("bulk_fulfil_confirm_title")}</h2>
                                <button className="modal-close" onClick={() => setConfirming(false)} aria-label={t("cancel")}>
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="modal-card-body">
                                <p className="bulk-labels-intro">{t("bulk_fulfil_confirm_intro", { n: String(queued.length), warehouse })}</p>
                                <div className="bulk-labels-table-wrap">
                                    <table className="bulk-labels-table" data-testid="bulk-fulfil-confirm-table">
                                        <thead>
                                            <tr>
                                                <th>{t("bulk_fulfil_col_order")}</th>
                                                <th>{t("bulk_fulfil_col_courier")}</th>
                                                <th>{t("bulk_fulfil_col_tracking")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {queued.map(row => (
                                                <tr key={row.id}>
                                                    <td>
                                                        <strong>{(row.target as ResolvedOrder).order_number}</strong>
                                                        {row.via && <span className="bulk-labels-muted"> {t("bulk_fulfil_via", { child: row.via })}</span>}
                                                    </td>
                                                    <td>{courierOf(row) || <span className="bulk-labels-status bulk-labels-status--warn">{t("bulk_fulfil_confirm_no_courier")}</span>}</td>
                                                    <td className="bulk-labels-file">{row.sheet.tracking_number.trim()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="modal-confirm-buttons" style={{ marginTop: 18 }}>
                                    <button className="btn btn-ghost dispatch-modal-btn" onClick={() => setConfirming(false)}>
                                        {t("cancel")}
                                    </button>
                                    <button className="btn btn-success dispatch-modal-btn" onClick={() => void apply()} disabled={queued.length === 0}>
                                        <PackageCheck size={14} />
                                        {t("bulk_fulfil_confirm_button", { n: String(queued.length) })}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkFulfilUploadModal;
